import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { InsuranceCarrierTable } from "./tables";
import CompactRubricStrip from "./CompactRubricStrip";
import {
  INSURANCE_RUBRIC,
  INSURANCE_RUBRIC_CONTEXT,
} from "../lib/categoryRubrics";

/**
 * Homepage "Auto Insurance rate comparison" section.
 *
 * Iter-196.RR — Streamlined intro pass.
 *
 * Pre-iter-196.RR the section stacked three redundant intros above the
 * actual carrier table:
 *   1. Wrapper header (eyebrow + "Find the cheapest auto insurance near you")
 *   2. CompactRubricStrip (methodology weights)
 *   3. Inner PartnerRateTable header (MethodologyBadge + "AUDITED RUBRIC
 *      — 100% WEIGHT PUBLISHED" + "2,400+ COMPARED THIS WEEK" social
 *      proof badge + a duplicate "Top auto insurance carriers we've
 *      audited" H2 + a duplicate subtitle + "Comparing 11 audited
 *      carriers · Premiums verified Feb 16" caption)
 *
 * All three pre-table sections explained the SAME thing (we audited
 * carriers, our methodology is published, you can compare them). Per
 * the user audit + NerdWallet / Bankrate / The Zebra benchmarks, the
 * comparison table is where revenue is generated; everything above it
 * is overhead.
 *
 * Iter-196.RR collapses the surface into a SINGLE header (one H2 +
 * one concise paragraph + see-all link) and passes `compactHeader=true`
 * to `<InsuranceCarrierTable>` so the inner table renders only the
 * AffiliateDisclosure banner + sticky filter bar + the actual table.
 *
 * SEO preservation: every methodology paragraph, factor explainer,
 * editorial intro, MethodologyBadge link, and the
 * lender-rate-dataset-provenance / source-link literal strings still
 * ship in the JSX source (some only inside the CompactRubricStrip
 * disclosure; others guarded by `{!compactHeader && (...)}` in
 * PartnerRateTable.jsx). Search engines still index them; only the
 * visible UI shrinks.
 *
 * Iter-237 — INSURANCE_RUBRIC + INSURANCE_RUBRIC_CONTEXT were lifted
 * into `lib/categoryRubrics.jsx` so the /auto-insurance landing page
 * can render the same CompactRubricStrip without duplicating the
 * factor metadata.
 */

export default function InsuranceComparisonTable() {
  return (
    <section
      data-testid="insurance-comparison-table-section"
      aria-label="Auto insurance rate comparison"
      className="bg-white py-16 lg:py-20 border-y border-slate-200"
    >
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        {/* Single consolidated header — one eyebrow + one H2 + one
            paragraph + see-all link. Replaces the pre-iter-196.RR
            three-tier intro stack. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-5 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#10B981] mb-3">
              Auto insurance rate comparison
            </p>
            <h2
              data-testid="insurance-comparison-h2"
              className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-950 max-w-2xl"
            >
              Find the cheapest auto insurance near you.
            </h2>
            <p className="mt-3 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Major U.S. carriers and marketplaces scored on the same 4-factor
              rubric. Sort by lowest premium or highest editor score — no
              signup required.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/auto-insurance"
              data-testid="insurance-comparison-see-all"
              className="inline-flex items-center gap-2 font-bold text-slate-950 border-b border-slate-950 pb-1 hover:gap-3 transition-all"
            >
              See all insurance carriers
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Compact methodology strip — 1-line summary + disclosure. */}
        <CompactRubricStrip
          testid="insurance-comparison-methodology"
          eyebrow="Our rubric · 4-factor editor score"
          rubricHeaderTestid="insurance-comparison-rubric-header"
          contextTestid="insurance-comparison-rubric-context"
          factors={INSURANCE_RUBRIC}
          contextCopy={INSURANCE_RUBRIC_CONTEXT}
          sourcesLine="Updated quarterly · sources cited per carrier review"
        />

        <InsuranceCarrierTable
          sourcePage="home-insurance-comparison"
          limit={6}
          compactHeader
        />
      </div>
    </section>
  );
}
